import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getRelease,
  getReleaseVersion,
  getVersionSyncStatus,
} from 'app/store/Releases/selectors';
import { fetchVersionTypesDDLEffect } from 'app/store/Releases/effects';
import { routes } from 'app/routing/routesConfig';
import { Outlet, useParams } from 'react-router-dom';
import { ReleaseHeader } from 'app/components/Releases';
import { LabelV2Release, ReleaseType } from 'types/releases';
import { ChakraAppLoader, ContentContainer } from 'app/components/Core';
import { ExportSendLabelV2Release } from 'app/components/Releases/ExportSendLabelV2Release';
import { TrackListNavigation } from 'app/components/Releases/TrackListNavigation';
import { NavTabs } from 'app/components/Core/ChakraNavTabs';
import { ContentContainerType } from 'types/enums';
import { Box } from '@chakra-ui/react';
import { useFetchRelease } from './hooks';
import { useDraftReleaseConnection, useReleaseExport } from '../Release/hooks';

export const ReleaseVersion = () => {
  const dispatch = useAppDispatch();
  const release = useAppSelector(getRelease);
  const releaseVersion = useAppSelector(getReleaseVersion);
  const { fetchRelease, loading } = useFetchRelease(release);
  const { guid, versionGuid } = useParams();
  const dataFetched = !loading && release && releaseVersion;

  const isSyncing =
    useAppSelector((state) =>
      getVersionSyncStatus(state, releaseVersion?.song.versionGuid || '')
    ) || false;

  useEffect(() => {
    fetchRelease();
  }, [fetchRelease, release?.guid]);

  useEffect(() => {
    dispatch(fetchVersionTypesDDLEffect());
  }, [dispatch]);

  const navTabRoutes = useMemo(() => {
    switch (release?.type) {
      case ReleaseType.TUNECORE:
        return [
          routes.releaseVersionDetails,
          routes.releaseVersionAudio,
          routes.releaseVersionCredits,
          routes.releaseVersionLyrics,
        ];
      case ReleaseType.LABEL_V2:
        return [
          routes.releaseVersionDetails,
          routes.releaseVersionAudio,
          routes.releaseVersionCredits,
          routes.releaseVersionLyrics,
          routes.releaseVersionSplits,
        ];
      case ReleaseType.SOUNDCLOUD:
        return [routes.releaseVersionDetails, routes.releaseVersionAudio];
      default:
        return [routes.releaseVersionDetails, routes.releaseVersionCredits];
    }
  }, [release]);

  const { exportConnexRelease } = useReleaseExport();
  const { handleOnConnect } = useDraftReleaseConnection();

  if (!dataFetched) {
    return <ChakraAppLoader />;
  }

  return (
    <>
      <ReleaseHeader
        release={release}
        version={{
          guid: releaseVersion.metadata.guid,
          type: releaseVersion.metadata.type,
          updated: releaseVersion.metadata.updated,
        }}
      >
        {release.type === ReleaseType.LABEL_V2 && (
          <ExportSendLabelV2Release
            exportRelease={exportConnexRelease}
            isReadyForExportReason={
              (release as LabelV2Release).isReadyForExportReason
            }
            handleOnConnect={handleOnConnect}
            releaseExportLink={{ href: '/' }}
            isInvalid={
              (isSyncing || (release as LabelV2Release).isInvalid) ?? true
            }
          />
        )}
      </ReleaseHeader>
      {release.type !== ReleaseType.LABEL ? (
        <TrackListNavigation />
      ) : (
        <Box mt={4} />
      )}
      <ContentContainer type={ContentContainerType.LIST} flex={0}>
        <NavTabs
          id="release-nav-tabs"
          routes={navTabRoutes}
          guid={{ guid: guid as string, versionGuid: versionGuid as string }}
          mb={6}
        />
      </ContentContainer>
      <Outlet />
    </>
  );
};
